import { createContext, useContext, type ReactNode } from "react";

import type { FeatureFlags, NetworkConfigResponse, Thresholds } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { describeError, useResource } from "../app/useResource";
import sentinelleMark from "../assets/brand/sentinelle-mark.svg";

/**
 * Configuration pilotée serveur (F0.5). Rien de spécifique à un réseau n'est
 * codé côté client : branding, emprise carte, groupes de lignes, seuils,
 * feature flags, régimes d'exploitation, vocabulaire carrefours — tout vient de
 * `GET /networks/{n}/config`.
 *
 * Le réseau chargé est `VITE_DEFAULT_NETWORK` s'il est défini, sinon le premier
 * renvoyé par `GET /networks`. Aucun identifiant de réseau en dur.
 */

interface ConfigContextValue {
  networkId: string;
  config: NetworkConfigResponse;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const backend = useBackend();
  const envDefault = import.meta.env.VITE_DEFAULT_NETWORK?.trim();

  const boot = useResource(async (signal): Promise<ConfigContextValue> => {
    const explicit = envDefault && envDefault.length > 0 ? envDefault : null;
    let networkId: string;
    if (explicit) {
      networkId = explicit;
    } else {
      const networks = await backend.api.listNetworks(signal);
      const first = networks.find((n) => n.enabled) ?? networks[0];
      if (!first) throw new Error("Aucun réseau disponible (GET /networks vide).");
      networkId = first.id;
    }
    const config = await backend.api.getConfig(networkId, signal);
    return { networkId, config };
  }, [backend, envDefault]);

  if (boot.loading) {
    return <BootScreen message="Chargement de la configuration…" />;
  }
  if (boot.error || !boot.data) {
    return <BootScreen message={describeError(boot.error)} tone="danger" onRetry={boot.reload} />;
  }

  return <ConfigContext.Provider value={boot.data}>{children}</ConfigContext.Provider>;
}

function useConfigContext(): ConfigContextValue {
  const value = useContext(ConfigContext);
  if (!value) throw new Error("useConfig doit être utilisé sous <ConfigProvider>");
  return value;
}

export function useNetworkId(): string {
  return useConfigContext().networkId;
}

export function useConfig(): NetworkConfigResponse {
  return useConfigContext().config;
}

export function useFeatureFlags(): FeatureFlags {
  return useConfigContext().config.featureFlags;
}

/** `true` si la fonctionnalité est activée pour ce réseau (F4.2). */
export function useFeatureFlag(name: keyof FeatureFlags): boolean {
  return useConfigContext().config.featureFlags[name];
}

export function useThresholds(): Thresholds {
  return useConfigContext().config.thresholds;
}

interface BootScreenProps {
  message: string;
  tone?: "info" | "danger";
  onRetry?: () => void;
}

function BootScreen({ message, tone = "info", onRetry }: BootScreenProps) {
  return (
    <section className="hero is-fullheight">
      <div className="hero-body">
        <div className="container has-text-centered">
          {/* Marque officielle (2026-09-11). */}
          <img src={sentinelleMark} alt="" style={{ height: 40, width: "auto", marginBottom: 10 }} />
          <p className="ovl mb-2">SENTINELLE DSS · SAE PCC</p>
          <p className={tone === "danger" ? "has-text-danger" : "has-text-grey"}>{message}</p>
          {onRetry ? (
            <button type="button" className="button is-small mt-4" onClick={onRetry}>
              Réessayer
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
